import { canonicalizeCidr, parseIp } from './ip';

/**
 * Policy routing: steering marked traffic into alternate routing tables.
 *
 * A firewall rule's `mark` action tags a packet; policy routing is the other half —
 * an `ip rule` that sends marked packets to look up a dedicated routing table
 * instead of `main`, and the routes that table holds. Together they express "voice
 * goes out the LTE uplink", "guest traffic never reaches the LAN", and so on.
 *
 * The whole feature lives in a namespace that is exclusively Connaro's, the same
 * safety-by-construction idea as the firewall's dedicated `inet connaro` table: a
 * reserved `ip rule` priority band and a reserved routing-table-id range. Nothing
 * outside that band or range is ever read or written, so applying — or tearing down
 * — a policy can never disturb the host's own `main` table or a rule some other
 * tool installed. The renderer emits an `ip -batch` script; the edge loads it
 * atomically and, on removal, flushes only the reserved band and range.
 */

/**
 * The `ip rule` preference band reserved for Connaro. Rules are numbered from the
 * base upward; the ceiling keeps them well below the kernel's default `main`/`default`
 * rules (priorities 32766/32767) so a policy rule is consulted first without ever
 * displacing the fallbacks that keep the host reachable.
 */
export const POLICY_ROUTING_PRIORITY_BASE = 20000;
export const POLICY_ROUTING_PRIORITY_CEILING = 29999;

/**
 * The routing-table-id range reserved for Connaro. Deliberately clear of the
 * reserved system tables (`local` 255, `main` 254, `default` 253, `unspec` 0) so a
 * policy table is always ours to flush.
 */
export const POLICY_ROUTING_TABLE_BASE = 20000;
export const POLICY_ROUTING_TABLE_CEILING = 29999;

/** An `ip rule` sending packets that carry `mark` to routing table `table`. */
export interface PolicyRoutingRule {
  /** The firewall mark to match — the same value a firewall rule's mark action sets. */
  mark: number;
  /** The reserved routing table to consult for a matched packet. */
  table: number;
  /** The `ip rule` preference; lower is consulted first. */
  priority: number;
  description?: string;
}

/** A route inside one of Connaro's reserved tables. */
export interface PolicyRoute {
  table: number;
  /** Destination prefix, or the string `default` for the table's default route. */
  destination: string;
  /** Next-hop gateway address. At least one of gateway/device is required. */
  gateway?: string;
  /** Egress device name. */
  device?: string;
  /** Drop traffic to this destination rather than routing it (used for isolation). */
  blackhole?: boolean;
  description?: string;
}

export interface PolicyRoutingConfig {
  rules: PolicyRoutingRule[];
  routes: PolicyRoute[];
}

/** Whether a mark/table/priority sits inside the reserved ranges. */
export const isReservedTable = (table: number): boolean =>
  Number.isInteger(table) &&
  table >= POLICY_ROUTING_TABLE_BASE &&
  table <= POLICY_ROUTING_TABLE_CEILING;

export const isReservedPriority = (priority: number): boolean =>
  Number.isInteger(priority) &&
  priority >= POLICY_ROUTING_PRIORITY_BASE &&
  priority <= POLICY_ROUTING_PRIORITY_CEILING;

/**
 * A device name safe to place in an `ip` command: interface names are short and
 * alphanumeric with a few separators. Rejecting everything else means a device
 * field can never carry a shell metacharacter or a second `ip` argument into the
 * batch, even though the batch is executed without a shell.
 */
const SAFE_DEVICE = /^[A-Za-z0-9_.@-]{1,32}$/;

/** Render a policy-routing config into an `ip -batch` script, or throw on an unsafe input.
 *
 * Every value is validated here rather than trusted from the caller: marks, tables,
 * and priorities must fall in the reserved ranges; a destination must be a canonical
 * prefix (or `default`); a gateway must be a bare IP; a device must be a plain
 * interface name. The output is deterministic — rules then routes, each sorted — so
 * the same config always renders identically and a diff is meaningful.
 */
export function renderIpBatch(config: PolicyRoutingConfig): string {
  const lines: string[] = [];

  const rules = [...config.rules].sort((a, b) => a.priority - b.priority || a.mark - b.mark);
  for (const rule of rules) {
    if (!isReservedPriority(rule.priority)) {
      throw new Error(`policy-routing: priority ${rule.priority} is outside the reserved band`);
    }
    if (!isReservedTable(rule.table)) {
      throw new Error(`policy-routing: table ${rule.table} is outside the reserved range`);
    }
    if (!Number.isInteger(rule.mark) || rule.mark <= 0 || rule.mark > 0xffffffff) {
      throw new Error(`policy-routing: mark ${rule.mark} is not a valid fwmark`);
    }
    lines.push(`rule add pref ${rule.priority} fwmark ${markHex(rule.mark)} table ${rule.table}`);
  }

  const routes = [...config.routes].sort(
    (a, b) => a.table - b.table || a.destination.localeCompare(b.destination),
  );
  for (const route of routes) {
    if (!isReservedTable(route.table)) {
      throw new Error(`policy-routing: table ${route.table} is outside the reserved range`);
    }
    const dest = normalizeDestination(route.destination);
    if (!dest) throw new Error(`policy-routing: "${route.destination}" is not a valid destination`);
    if (route.blackhole) {
      // A blackhole route drops matched traffic and takes no next hop.
      lines.push(`route add blackhole ${dest} table ${route.table}`);
      continue;
    }
    const nexthop = renderNexthop(route);
    lines.push(`route add ${dest} ${nexthop} table ${route.table}`);
  }

  return lines.join('\n') + '\n';
}

function renderNexthop(route: PolicyRoute): string {
  const parts: string[] = [];
  if (route.gateway !== undefined && route.gateway !== '') {
    const ip = parseIp(route.gateway);
    if (!ip) throw new Error(`policy-routing: gateway "${route.gateway}" is not an IP address`);
    parts.push(`via ${route.gateway}`);
  }
  if (route.device !== undefined && route.device !== '') {
    if (!SAFE_DEVICE.test(route.device)) {
      throw new Error(`policy-routing: device "${route.device}" is not a valid interface name`);
    }
    parts.push(`dev ${route.device}`);
  }
  if (parts.length === 0) {
    throw new Error('policy-routing: a route needs a gateway or a device');
  }
  return parts.join(' ');
}

/** `default`, or a canonical CIDR. A bare address is treated as a host route (/32 or /128). */
function normalizeDestination(destination: string): string | null {
  if (destination === 'default') return 'default';
  if (destination.includes('/')) return canonicalizeCidr(destination);
  const ip = parseIp(destination);
  if (!ip) return null;
  return `${destination}/${ip.family === 4 ? 32 : 128}`;
}

/** fwmarks are conventionally written in hex in `ip rule`; render as an integer in hex. */
function markHex(mark: number): string {
  return `0x${Math.floor(mark).toString(16)}`;
}

// ---------------------------------------------------------------------------
// Template library
// ---------------------------------------------------------------------------

/**
 * The named policy-routing patterns Phase 9 calls for. A template is a small,
 * reviewed recipe an operator instantiates with a couple of parameters rather than
 * assembling rules and routes by hand — the common intents where getting the raw
 * `ip rule`/`ip route` wrong strands traffic.
 */
export const POLICY_ROUTING_TEMPLATES = [
  'VOICE',
  'BACKUPS',
  'CENTRAL_BREAKOUT',
  'LOCAL_BREAKOUT',
  'DEDICATED_MANAGEMENT',
  'GUEST_ISOLATION',
] as const;
export type PolicyRoutingTemplate = (typeof POLICY_ROUTING_TEMPLATES)[number];

/** What a template needs to expand: the mark it steers, the table it fills, and a next hop. */
export interface TemplateParams {
  mark: number;
  table: number;
  priority: number;
  /** Next-hop gateway for the steered traffic (e.g. the uplink or breakout gateway). */
  gateway?: string;
  /** Egress device for the steered traffic. */
  device?: string;
  /** Prefixes the template applies to — the destinations it routes, or the guest space it isolates. */
  prefixes?: string[];
}

/**
 * Expand a template into concrete rules and routes. Every template installs one
 * `ip rule` binding the mark to the table; they differ in what routes fill the
 * table. `GUEST_ISOLATION` is the exception with no next hop: it blackholes the
 * protected prefixes so guest-marked traffic to them is dropped rather than routed,
 * which is the whole point — an isolation policy that fell through to `main` would
 * not isolate anything.
 */
export function expandTemplate(
  template: PolicyRoutingTemplate,
  params: TemplateParams,
): PolicyRoutingConfig {
  const rule: PolicyRoutingRule = {
    mark: params.mark,
    table: params.table,
    priority: params.priority,
    description: template,
  };

  switch (template) {
    case 'GUEST_ISOLATION': {
      // Drop guest traffic toward the protected prefixes; anything else the guest
      // sends is unmatched by this table and falls back to normal routing.
      const routes: PolicyRoute[] = (params.prefixes ?? []).map((p) => ({
        table: params.table,
        destination: p,
        blackhole: true,
        description: 'guest-isolation',
      }));
      return { rules: [rule], routes };
    }
    case 'VOICE':
    case 'BACKUPS':
    case 'CENTRAL_BREAKOUT':
    case 'LOCAL_BREAKOUT':
    case 'DEDICATED_MANAGEMENT': {
      // Steer the template's prefixes (or the default route, if none given) out the
      // chosen next hop. Voice/backups/breakout differ in intent and in which mark
      // and gateway an operator picks, not in shape: each sends its marked traffic
      // to a next hop the main table would not have chosen.
      const destinations =
        params.prefixes && params.prefixes.length > 0 ? params.prefixes : ['default'];
      const routes: PolicyRoute[] = destinations.map((destination) => ({
        table: params.table,
        destination,
        ...(params.gateway ? { gateway: params.gateway } : {}),
        ...(params.device ? { device: params.device } : {}),
        description: template.toLowerCase(),
      }));
      return { rules: [rule], routes };
    }
  }
}
